/**
 * Practice-yard combat — pure, so it can be tested without a renderer.
 *
 * The yard is CLIENT-ONLY. Real combat is server-authoritative (the arena room
 * resolves every hit), but a practice dummy has no opponent, no loot and no
 * stakes, so nothing here touches the server or the player's real inventory.
 * The numbers are for feel, not balance — they are not the real weapon stats.
 */
import type { Vec2 } from '../types';

export type PracticeWeaponKey = 'hunting_knife' | 'magnum';

export interface PracticeWeapon {
  key: PracticeWeaponKey;
  name: string;
  kind: 'MELEE' | 'FIREARM';
  /** Reach, metres. */
  arcLength: number;
  /** Full cone angle, degrees. */
  arcWidth: number;
  damage: number;
  /** Minimum time between attacks, ms. */
  cooldownMs: number;
  /** Firearms spend a round per shot. */
  usesAmmo: boolean;
}

export const PRACTICE_WEAPONS: Record<PracticeWeaponKey, PracticeWeapon> = {
  hunting_knife: {
    key: 'hunting_knife', name: 'Hunting knife', kind: 'MELEE',
    arcLength: 1.9, arcWidth: 110, damage: 18, cooldownMs: 450, usesAmmo: false,
  },
  magnum: {
    key: 'magnum', name: 'Magnum', kind: 'FIREARM',
    arcLength: 16, arcWidth: 7, damage: 34, cooldownMs: 380, usesAmmo: true,
  },
};

/** Rounds in the practice magazine. */
export const PRACTICE_AMMO = 12;

export interface PracticeTarget {
  id: string;
  position: Vec2;
  /** Body radius, metres. */
  radius: number;
}

/**
 * Whether an attack from `origin` facing `facing` lands on `target`.
 *
 * `facing` is a yaw in radians with 0 = +Z, the convention the sim uses, so
 * the forward vector is (sin yaw, cos yaw). The target's own radius widens the
 * cone (a thick dummy is easier to hit at range, as in real life) and extends
 * the reach by the same amount.
 */
export function attackHits(
  origin: Vec2,
  facing: number,
  weapon: Pick<PracticeWeapon, 'arcLength' | 'arcWidth'>,
  target: PracticeTarget,
): boolean {
  const dx = target.position.x - origin.x;
  const dz = target.position.z - origin.z;
  const dist = Math.hypot(dx, dz);
  if (dist > weapon.arcLength + target.radius) return false;
  if (dist <= target.radius) return true; // standing inside it
  const toTarget = Math.atan2(dx, dz);
  let diff = Math.abs(toTarget - facing) % (Math.PI * 2);
  if (diff > Math.PI) diff = Math.PI * 2 - diff;
  const half = (weapon.arcWidth * Math.PI) / 360;
  const widen = Math.asin(Math.min(1, target.radius / dist));
  return diff <= half + widen;
}

/** The nearest target an attack lands on, or null. A bullet stops at the first body it meets. */
export function firstHit(
  origin: Vec2,
  facing: number,
  weapon: Pick<PracticeWeapon, 'arcLength' | 'arcWidth'>,
  targets: readonly PracticeTarget[],
): PracticeTarget | null {
  let best: PracticeTarget | null = null;
  let bestDist = Infinity;
  for (const t of targets) {
    if (!attackHits(origin, facing, weapon, t)) continue;
    const d = Math.hypot(t.position.x - origin.x, t.position.z - origin.z);
    if (d < bestDist) { best = t; bestDist = d; }
  }
  return best;
}
